// VIS-267 W6: the core ground paint (main.js paintGround) off the main thread. The canvas is ~4,000 gradient fills at
// 3 px/m (Aurora: 3600x3300) plus two 1 px/m splat maps; painted on the main thread it was a long task in the
// middle of the start and held the canvas next to its 63 MB texture. paintAsync() paints it in a module worker
// (groundpaint-worker.js) on OffscreenCanvases and gets the pictures back as ImageBitmaps (moved, not copied), which
// the textures upload and then close (main.js freeGround6). Where a worker cannot paint 2D (Safari before 16.4, no
// module workers) the same paintGround() runs on the main thread with DOM canvases: one paint, two places to run it.
// Worker-safe: no THREE and no DOM in paintGround(); the caller passes everything as plain data (main.js groundInput)
// and a canvas factory. The far landscape reads the result back from `probe` (landscape.js coreTex): the four 5 px
// border strips and the 10 m copy it used to read from the canvas itself.
// WS-63 dithering note: a GPU canvas dithers every gradient with the same screen-aligned pattern; with a calm-snow look
// (CALM) the snow blobs go on a 1 px/m software canvas (willReadFrequently) and are drawn in once, here as before.
// VIS-267 step 4 (an iPhone 13 Pro lost its graphics in a season switch right after its paint: 7 canvases, 70.5 MB, and
// the main thread's retry made them again): the core canvas is painted in BANDS of rows on one band-high canvas at a
// time (Aurora: 3600 x 672, 9.7 MB, not 3600 x 3300, 47.5 MB). Every band runs the whole paint moved up (the random
// stream from its start; the splat maps and the 1 m pictures painted once, with the first band) and keeps its middle
// rows, which go straight into the ground picture (liteWriter). MARGIN rows above and below cover the widest blur (a
// marsh's 5 m soft edge, 15 px) and put every band's top on a multiple of 32 rows (the gradients' dither pattern).
import { blurDraw } from './canvasblur.js'; // Safari/Mac ground paint: the ctx.filter blur, or a canvas shadow where Safari ignores it
/** VIS-267: the ready-made ground picture's format version, in every file and print (groundbake.js groundPrint).
 *  Raise it only when the file format or the print's formula changes; a change to the paint code (this file and what
 *  it imports) changes every print by itself (groundbake.js PAINT_CODE). 2: the print carries PAINT_CODE. */
export const PAINT_VERSION = 2;

export const DETAIL_LAYERS = ['lichen', 'shrub', 'litter', 'grass', 'gravel', 'sand']; // (ground.js re-exports it)
const weightsOf = (base) => DETAIL_LAYERS.map((k) => base?.[k] ?? 0);
/** main.js hash01: the same numbers (the marsh's sedge patches) */
export const hash01 = (x, z) => Math.abs(Math.sin(x * 127.1 + z * 311.7) * 43758.5453) % 1;
const free = (c) => { if (c) c.width = c.height = 1; }; // a canvas let go at once (not when the collector comes)

/**
 * Canopy shade for the core ground canvas: crown area per metre cell as a 1 px/m picture of a cool shade (snow under a
 * closed canopy is in shade most of the day, and dense stands read as a darker mass from above), drawn in blurred
 * (drawCanopy: into every band, VIS-267 step 4), and the needle litter in the splat map (WS-42).
 * ctx: { PX (texels per metre), extent, trees(fn(x, z, r)), strength, shade, ctl, ctlGain, ctlLayer, mk (canvas factory) }
 * -> { c (the 1 px/m picture), blur (px), color }
 */
export function canopyShade(ctx) {
  const { PX, extent: ex } = ctx;
  const look = { canopyShade: ctx.strength ?? 0.5, colors: { shade: ctx.shade || 'rgba(112,128,158,1)' } };
  const W = Math.round(ex.maxX - ex.minX), H = Math.round(ex.maxZ - ex.minZ);
  const acc = new Float32Array(W * H);
  ctx.trees((x, z, r) => {
    const i = Math.floor(x - ex.minX), j = Math.floor(z - ex.minZ), rr = Math.ceil(r * 1.3);
    for (let a = -rr; a <= rr; a++) for (let b = -rr; b <= rr; b++) {
      const u = i + a, v = j + b;
      if (u < 0 || v < 0 || u >= W || v >= H || a * a + b * b > rr * rr) continue;
      acc[v * W + u] += 1;
    }
  });
  const c = ctx.mk(W, H);
  const cg = c.getContext('2d'), img = cg.createImageData(W, H);
  const [sr, sg, sb] = (look.colors.shade.match(/\d+/g) || [112, 128, 158]).map(Number);
  for (let k = 0; k < W * H; k++) {
    const v = Math.min(1, acc[k] / 1.6);
    img.data[k * 4] = sr; img.data[k * 4 + 1] = sg; img.data[k * 4 + 2] = sb; img.data[k * 4 + 3] = Math.round(255 * v * look.canopyShade * 0.55);
  }
  cg.putImageData(img, 0, 0);
  ctx.ctl?.density(acc, W, H, ctx.ctlLayer || 'litter', ctx.ctlGain ?? 0.4); // WS-42: needle litter + moss under the crowns
  return { c, blur: +(1.6 * PX).toFixed(1), color: `rgb(${sr},${sg},${sb})` };
}
function drawCanopy(g, cw, ch, cs) {
  g.save(); g.imageSmoothingEnabled = true;
  blurDraw(g, cs.blur, (h) => h.drawImage(cs.c, 0, 0, cw, ch), cs.color); // Safari/Mac ground paint (canvasblur.js)
  g.restore();
}

/** Soft compacted-snow underlay for roads, paths and trails (blurred, no hard edges). It matters from far away
 *  (texture minification) and for the far landscape, which takes its colours from this canvas; the ribbon mesh
 *  draws the detail. ctx: { g, PX, extent, data, look, sc (a scratch canvas of g's own size), dy (g's first row) }
 *  VIS-267 step 4 (a study: 92 % of the paint's time): each colour's ways are stroked sharp onto the scratch and drawn
 *  in blurred ONCE, not one blurred full-canvas draw per way (44 at Aurora: 5.8 s on a processor-drawn canvas, 0.8 s
 *  now). The picture is the same but where ways of a colour meet (one blurred union instead of blurred strokes over
 *  each other: under the 3D roads). The scratch is g's size, so a way past its edge is cut there, as on g. */
export function paintRoads(ctx) {
  const { g, PX, extent: ex, data, look, sc, dy = 0 } = ctx;
  const tx = (x) => (x - ex.minX) * PX, tz = (z) => (z - ex.minZ) * PX;
  const h = sc.getContext('2d'), blur = +(0.8 * PX).toFixed(1);
  const layer = (color, list) => {
    if (!list.length) return;
    h.setTransform(1, 0, 0, 1, 0, 0); h.clearRect(0, 0, sc.width, sc.height); h.setTransform(1, 0, 0, 1, 0, -dy);
    h.lineCap = 'round'; h.lineJoin = 'round'; h.strokeStyle = color;
    for (const [pts, w] of list) { h.beginPath(); pts.forEach(([x, z], i) => (i ? h.lineTo(tx(x), tz(z)) : h.moveTo(tx(x), tz(z)))); h.lineWidth = w; h.stroke(); }
    blurDraw(g, blur, (gg) => gg.drawImage(sc, 0, dy), color); // Safari/Mac ground paint (canvasblur.js): the image in its colour's shadow
  };
  layer(look.colors.road, data.roads.map((r) => [r.pts, (r.width ?? look.roadWidth[r.kind] ?? look.roadWidth.default) * PX])); // WS-58 r.width: a path drawn by hand
  layer(look.colors.path, data.paths.map((p) => [p.pts, (p.width ?? (p.kind === 'track' ? look.trackWidth : look.pathWidth)) * PX * 0.9]));
  layer(look.colors.trail, data.trails.filter((t) => look.trails || t.drawn).map((t) => [t.pts, (t.width ?? (t.kind === 'ski' ? 3.2 : 1.6)) * PX]));
}

/** WS-42 control (splat) map painter: two opaque canvases at `PX` texels per metre whose RGB channels hold the six
 *  layer weights (canvas 1 = lichen, shrub, litter; canvas 2 = grass, gravel, sand). Painting layer k with alpha a
 *  blends BOTH canvases the same way (the other channels towards 0), so the six weights always sum to 1 and canvas blur
 *  filters act on all of them alike. `canvases` = the two (main.js makes their textures). */
export function createControl(extent, base, PX, mk) {
  const W = Math.round((extent.maxX - extent.minX) * PX), H = Math.round((extent.maxZ - extent.minZ) * PX);
  const cv = [mk(W, H), mk(W, H)];
  const g = cv.map((c) => c.getContext('2d'));
  const w = weightsOf(base), sum = w.reduce((a, b) => a + b, 0) || 1;
  g[0].fillStyle = `rgb(${w.slice(0, 3).map((v) => Math.round((v / sum) * 255)).join(',')})`; g[0].fillRect(0, 0, W, H);
  g[1].fillStyle = `rgb(${w.slice(3).map((v) => Math.round((v / sum) * 255)).join(',')})`; g[1].fillRect(0, 0, W, H);
  const tx = (x) => (x - extent.minX) * PX, tz = (z) => (z - extent.minZ) * PX;
  const rgb = (k, c) => (Math.floor(k / 3) === c ? [0, 1, 2].map((i) => (i === k % 3 ? 255 : 0)) : [0, 0, 0]).join(',');
  const idx = (layer) => (typeof layer === 'number' ? layer : DETAIL_LAYERS.indexOf(layer));
  const each = (layer, fn) => { const k = idx(layer); if (k < 0) return; for (let c = 0; c < 2; c++) { g[c].save(); fn(g[c], rgb(k, c)); g[c].restore(); } };
  const pathOn = (gg, pts, close) => { gg.beginPath(); pts.forEach(([x, z], i) => (i ? gg.lineTo(tx(x), tz(z)) : gg.moveTo(tx(x), tz(z)))); if (close) gg.closePath(); };
  const api = {
    PX, W, H, canvases: cv,
    /** soft blob (mottle patch): full weight inside `core` of the radius, fading to 0 at r */
    blob(x, z, r, layer, a = 1, core = 0.45) {
      each(layer, (gg, c) => { const X = tx(x), Y = tz(z), R = r * PX, grd = gg.createRadialGradient(X, Y, 0, X, Y, R);
        grd.addColorStop(0, `rgba(${c},${a})`); grd.addColorStop(core, `rgba(${c},${a})`); grd.addColorStop(1, `rgba(${c},0)`);
        gg.fillStyle = grd; gg.fillRect(X - R, Y - R, 2 * R, 2 * R); });
    },
    // Safari/Mac ground paint: blurDraw (canvasblur.js)
    poly(pts, layer, a = 1, blur = 0) { each(layer, (g0, c) => blurDraw(g0, blur && +(blur * PX).toFixed(1), (gg) => { pathOn(gg, pts, true); gg.fillStyle = `rgba(${c},${a})`; gg.fill(); })); },
    line(pts, width, layer, a = 1, blur = 0, closed = false) {
      each(layer, (g0, c) => blurDraw(g0, blur && +(blur * PX).toFixed(1), (gg) => { pathOn(gg, closed ? [...pts, pts[0]] : pts, false);
        gg.lineCap = gg.lineJoin = 'round'; gg.lineWidth = width * PX; gg.strokeStyle = `rgba(${c},${a})`; gg.stroke(); }));
    },
    /** VIS-267 step 4: many lines of one layer, alpha and blur ([pts, width] each): stroked sharp onto one scratch and
     *  drawn in blurred once a canvas (as paintRoads), not one blurred whole-map draw per way; the scratch goes after */
    lines(list, layer, a = 1, blur = 0) {
      const k = idx(layer), px = blur && +(blur * PX).toFixed(1);
      if (k < 0 || !list.length) return;
      if (!px || list.length === 1) { for (const [pts, width] of list) api.line(pts, width, layer, a, blur); return; } // (nothing to share: as before)
      const sc = mk(W, H), h = sc.getContext('2d');
      for (let c = 0; c < 2; c++) {
        const col = rgb(k, c);
        h.clearRect(0, 0, W, H); h.lineCap = h.lineJoin = 'round'; h.strokeStyle = `rgba(${col},${a})`;
        for (const [pts, width] of list) { pathOn(h, pts, false); h.lineWidth = width * PX; h.stroke(); }
        g[c].save(); blurDraw(g[c], px, (gg) => gg.drawImage(sc, 0, 0), `rgb(${col})`); g[c].restore();
      }
      free(sc);
    },
    /** density field (1 m cells over the extent, e.g. crown cover) -> layer weight min(1, d / 1.6) * gain, blurred */
    density(acc, w, h, layer, gain = 1, blur = 1.2) {
      const k = idx(layer); if (k < 0) return;
      const c = mk(w, h);
      const cg = c.getContext('2d'), img = cg.createImageData(w, h);
      for (let cc = 0; cc < 2; cc++) {
        const col = rgb(k, cc).split(',').map(Number);
        for (let i = 0; i < w * h; i++) { const v = Math.min(1, acc[i] / 1.6) * gain; img.data[i * 4] = col[0]; img.data[i * 4 + 1] = col[1]; img.data[i * 4 + 2] = col[2]; img.data[i * 4 + 3] = Math.round(255 * v); }
        cg.putImageData(img, 0, 0);
        blurDraw(g[cc], +(blur * PX).toFixed(1), (h) => h.drawImage(c, 0, 0, W, H), `rgb(${col})`); // Safari/Mac ground paint (canvasblur.js)
      }
      free(c); // VIS-267 step 4: let go at once
    },
  };
  return api;
}

/** VIS-267 step 4: the band height and the rows above and below it each band paints and drops (see the top). */
export const BAND = 480, MARGIN = 96;
/**
 * The core ground (main.js groundInput says what goes in). mk(w, h) = a canvas (OffscreenCanvas in the worker).
 * -> { lite (the ground picture, liteWriter), ctl: [canvas, canvas] | null (the season's splat maps, when asked for),
 *      probe | null (with a far landscape) }. opts (tests, QA): { band } rows a band (a canvas no higher than
 *      band + 2 MARGIN rows is painted in one).
 */
export function paintGround(I, mk, { band = BAND } = {}) {
  const { W, D, extent, PX, P, look: GLOOK, ground: GROUND, season: SEASON, ways: RD } = I;
  const cw = Math.round(W * PX), ch = Math.round(D * PX);
  const tx = (x) => (x - extent.minX) * PX, tz = (z) => (z - extent.minZ) * PX;
  let s = 99;
  const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const bounds = (pts) => { const xs = pts.map((p) => p[0]), zs = pts.map((p) => p[1]); return [Math.min(...xs), Math.max(...xs), Math.min(...zs), Math.max(...zs)]; };
  // WS-42: the control (splat) map for the bare-ground detail layers, painted alongside from the same layout (VIS-267
  // step 4: with the first band; its canvases are 1 px a metre, whole)
  const GL = I.ctl, CTL = GL ? createControl(extent, GL.base, 1, mk) : null; // WS-41: a season's control map is painted once (main.js asks only for a season without one)
  const AREA_LAYER = { farmland: 'grass', meadow: 'grass', grass: 'grass', marsh: 'grass', parking: 'gravel', helipad: 'gravel', ...(GL?.areas || {}) };
  const CALM = I.calm, T = I.trees; // T: x, z, r (vegetation) or x, z, scale (the trees above) per tree shown
  let wc = null, sWinter = 0, shade = null, sc = null; // painted once for every band: the calm snow and the canopy shade (1 px/m); the ways' scratch
  /** the whole paint on g, whose first row is the canvas's row dy; `first`: the band that paints the splat maps */
  function core(g, first, dy) {
    const path = (pts) => { g.beginPath(); pts.forEach(([x, z], i) => (i ? g.lineTo(tx(x), tz(z)) : g.moveTo(tx(x), tz(z)))); };
    const ring = (pts) => { path(pts); g.closePath(); };
    const out = (a, b) => b < dy || a > dy + g.canvas.height; // VIS-267 step 4: a shape's rows [a, b] miss this band (it is not drawn here; its random numbers are drawn all the same)
    if (dy) g.translate(0, -dy);
    g.fillStyle = P.base;
    g.fillRect(0, 0, cw, ch);
    // WS-35: bare-ground mottle (lichen, heather, crowberry, grass patches) in the season's tones
    if (P.mottle) for (const m of P.mottle) for (let i = 0; i < (m.n ?? 1200); i++) {
      const x = rnd() * cw, y = rnd() * ch, r = ((m.r?.[0] ?? 4) + rnd() * ((m.r?.[1] ?? 30) - (m.r?.[0] ?? 4))) * PX;
      if (first && CTL && m.layer) CTL.blob(extent.minX + x / PX, extent.minZ + y / PX, r / PX, m.layer, Math.min(1, m.ctl ?? 0.8)); // WS-42: same patch in the splat
      if (out(y - r, y + r)) continue;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, `rgba(${m.tone},${m.a ?? 0.15})`); grd.addColorStop(1, `rgba(${m.tone},0)`);
      g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // Wind-blown variation in the snow.
    // WS-63 owner fix: a GPU canvas dithers every gradient with the same screen-aligned pattern, so 2600 faint blobs
    // added it up into rows of +-2 levels: straight streaks on calm snow once the ripples no longer hide them. With a
    // WS-63 snow version (ground.look.snow.pattern / grain) they go on a 1 px/m software canvas (its dither does not add
    // up), drawn once 3x; same random stream. Absent = as before. (VIS-267 step 4: that canvas is painted with the first
    // band and drawn into every band; the others take the random stream from where it ended.)
    const blobs = (wg, ws, top, bot) => {
      for (let i = 0; i < 2600; i++) {
        const x = rnd() * cw * ws, y = rnd() * ch * ws, r = (10 + rnd() * 40) * PX * ws;
        const tone = rnd() > 0.5 ? '255,255,255' : '190,205,228';
        if (y + r < top || y - r > bot) continue; // VIS-267 step 4: not in this band
        const grd = wg.createRadialGradient(x, y, 0, x, y, r);
        grd.addColorStop(0, `rgba(${tone},0.10)`); grd.addColorStop(1, `rgba(${tone},0)`);
        wg.fillStyle = grd; wg.fillRect(x - r, y - r, r * 2, r * 2);
      }
    };
    if (P.winter && CALM) {
      if (!wc) { wc = mk(W, D); blobs(wc.getContext('2d', { willReadFrequently: true }), 1 / PX, -Infinity, Infinity); sWinter = s; } else s = sWinter;
      g.drawImage(wc, 0, 0, cw, ch);
    } else if (P.winter) blobs(g, 1, dy, dy + g.canvas.height);
    // Blue shade and needle litter under the canopy.
    if (I.veg) { // WS-16: survey trees, as a density field (VIS-199: a planted tree shades too)
      shade ||= canopyShade({ PX, extent, mk, strength: GLOOK?.canopyShade, shade: GLOOK?.colors.shade, ctl: CTL, ctlGain: GL?.canopy ?? 0.4, trees: (fn) => { for (let i = 0; i < T.length; i += 3) fn(T[i], T[i + 1], T[i + 2]); } });
      drawCanopy(g, cw, ch, shade);
    } else for (let i = 0; i < T.length; i += 3) { // WS-14: the trees actually shown (treeRules), not the raw baked list
      const x = tx(T[i]), y = tz(T[i + 1]), r = 4.5 * PX * T[i + 2];
      if (out(y - r, y + r)) continue;
      const grd = g.createRadialGradient(x, y, 0, x, y, r);
      grd.addColorStop(0, 'rgba(150,165,190,0.22)'); grd.addColorStop(1, 'rgba(150,165,190,0)');
      g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
    }
    for (const a of I.areas) {
      const fill = P.areas[a.kind]; // WS-35: per-season area fills (winter: the original snow tones)
      if (!fill) continue;
      // WS-42: on bare ground a bog has no drawn edge (its straight survey outline read as a line); palette.areaBlur = { kind: m }
      const soft = P.winter ? 0 : (P.areaBlur?.[a.kind] ?? (a.kind === 'marsh' ? 5 : 0));
      blurDraw(g, soft && +(soft * PX).toFixed(1), (h) => { ring(a.outer); h.fillStyle = fill; h.fill(); }); // Safari/Mac ground paint: the ctx.filter blur, or a canvas shadow where Safari ignores it (canvasblur.js)
      if (first && CTL && AREA_LAYER[a.kind]) CTL.poly(a.outer, AREA_LAYER[a.kind], a.kind === 'marsh' ? 0.7 : 1, soft || 0.8); // WS-42
      if (a.kind === 'marsh') {
        // Sedge tufts poking through the snow.
        const [x0, x1, z0, z1] = bounds(a.outer);
        g.save(); ring(a.outer); g.clip();
        const k = PX / 3; // VIS-267: a tuft is as big in metres at every density (drawn 1 px wide at 3 texels a metre)
        g.strokeStyle = P.sedge; g.lineWidth = k; // WS-35
        // WS-17: water.wetland = 'snow' (frozen bog under snow, winter drone photos): only a few clumps of sedge show,
        // softer; 3000 even strokes read as a speckle pattern (round-4c feedback). Same random sequence either way.
        const wet = I.water?.wetland === 'snow';
        const vn = (u, v) => { const i = Math.floor(u), j = Math.floor(v), f = u - i, e = v - j, h = (a, b) => hash01(a * 7.13, b * 3.71), s1 = f * f * (3 - 2 * f), s2 = e * e * (3 - 2 * e);
          return (h(i, j) * (1 - s1) + h(i + 1, j) * s1) * (1 - s2) + (h(i, j + 1) * (1 - s1) + h(i + 1, j + 1) * s1) * s2; };
        if (wet) g.strokeStyle = P.sedgeWet; // WS-35
        for (let i = 0; i < 3000; i++) {
          const x = tx(x0 + rnd() * (x1 - x0)), y = tz(z0 + rnd() * (z1 - z0)), ddx = (rnd() - 0.5) * 3 * k, ddy = (2 + rnd() * 4) * k;
          if (out(y - ddy - 2 * k, y + 2 * k) ||(wet && vn(x / PX / 14, y / PX / 14) * 0.7 + vn(x / PX / 4, y / PX / 4) * 0.3 < 0.66)) continue;
          g.beginPath(); g.moveTo(x, y); g.lineTo(x + ddx, y - ddy); g.stroke();
        }
        g.restore();
      }
    }
    // Frozen river, channel and ponds: snow-dusted ice with darker wind-swept streaks.
    for (const w of I.waters) {
      const [x0, x1, z0, z1] = bounds(w.outer);
      g.save();
      g.beginPath();
      for (const r of [w.outer, ...w.holes]) { r.forEach(([x, z], i) => (i ? g.lineTo(tx(x), tz(z)) : g.moveTo(tx(x), tz(z)))); g.closePath(); }
      g.fillStyle = I.water ? P.water : '#cbdbeb'; g.fill('evenodd'); // WS-35: P.water = season underlay // WS-14 4b: with real water the shader draws ice / open water
      g.clip('evenodd');
      if (I.water) { /* no painted texture: water.js shades it */ } else
      if (GROUND.iceStreaks === false) { // WS-14: snow-dusted ice as soft drifts, no stroke texture (F6)
        for (let i = 0; i < 260; i++) {
          const x = tx(x0 + rnd() * (x1 - x0)), y = tz(z0 + rnd() * (z1 - z0)), r = (6 + rnd() * 18) * PX;
          if (out(y - r, y + r)) continue;
          const grd = g.createRadialGradient(x, y, 0, x, y, r);
          grd.addColorStop(0, 'rgba(255,255,255,0.35)'); grd.addColorStop(1, 'rgba(255,255,255,0)');
          g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
        }
      } else
      for (let i = 0; i < 900; i++) {
        const x = tx(x0 + rnd() * (x1 - x0)), y = tz(z0 + rnd() * (z1 - z0)), col = rnd() > 0.5 ? 'rgba(150,182,215,0.35)' : 'rgba(255,255,255,0.6)';
        const lw = (1 + rnd() * 3) * PX, len = (8 + rnd() * 20) * PX, rise = (rnd() - 0.5) * 6 * PX; // (the random numbers in the order they always came)
        if (out(Math.min(y, y + rise) - lw, Math.max(y, y + rise) + lw)) continue;
        g.strokeStyle = col; g.lineWidth = lw;
        g.beginPath(); g.moveTo(x, y); g.lineTo(x + len, y + rise); g.stroke();
      }
      g.restore();
      if (GROUND.waterOutline !== false && !I.water) { path([...w.outer, w.outer[0]]); g.strokeStyle = 'rgba(120,150,185,0.7)'; g.lineWidth = 1.2 * PX; g.stroke(); }
    }
    // WS-14: optional zone ground paint (zones[].paint = { fill, stroke, width }), inside the core extent.
    for (const z of I.zones) {
      for (const r of z.rings) {
        ring(r);
        if (z.paint.fill) { g.fillStyle = z.paint.fill; g.fill(); }
        if (z.paint.stroke) { g.strokeStyle = z.paint.stroke; g.lineWidth = (z.paint.width || 0.6) * PX; g.stroke(); }
      }
    }
    g.lineCap = 'round'; g.lineJoin = 'round';
    g.strokeStyle = P.streams; g.lineWidth = 2 * PX; // WS-35
    for (const st of I.streams) { path(st); g.stroke(); }
    if (first && CTL) { // WS-42: gravel under the roads (+ verge), sandy earth under the paths (VIS-267 step 4: blurred once each)
      CTL.lines(RD.roads.map((r) => [r.pts, (r.width ?? GLOOK?.roadWidth[r.kind] ?? GLOOK?.roadWidth.default ?? 5.6) + 1.2]), 'gravel', 1, 0.6);
      CTL.lines(RD.paths.map((p) => [p.pts, (p.width ?? GLOOK?.pathWidth ?? 1.5) + 0.4]), 'sand', 0.9, 0.4);
    }
    if (GLOOK) { // WS-16: soft underlay; the ribbon mesh draws roads, paths and trails
      if (!sc || sc.width !== g.canvas.width || sc.height !== g.canvas.height) { free(sc); sc = mk(g.canvas.width, g.canvas.height); }
      paintRoads({ g, PX, extent, data: RD, look: GLOOK, sc, dy });
    } else {
    // Ploughed roads with tyre tracks.
    for (const r of RD.roads) {
      const w = r.width ?? (r.kind === 'service' ? 4.2 : 6.2);
      path(r.pts); g.strokeStyle = '#ccd6e2'; g.lineWidth = (w + 1.4) * PX; g.stroke();
      path(r.pts); g.strokeStyle = '#e4e9f0'; g.lineWidth = w * PX; g.stroke();
      for (const off of [-0.9, 0.9]) {
        g.save(); g.translate(off * PX, off * PX * 0.3);
        path(r.pts); g.strokeStyle = 'rgba(170,185,205,0.55)'; g.lineWidth = 0.45 * PX; g.stroke();
        g.restore();
      }
    }
    // Footpaths: packed snow with a dashed blue guide like the printed map.
    for (const p of RD.paths) { path(p.pts); g.strokeStyle = '#e1e8f1'; g.lineWidth = (p.width ?? 1.8) * PX; g.stroke(); }
    g.setLineDash([2.6 * PX, 2 * PX]);
    g.strokeStyle = 'rgba(110,150,205,0.8)'; g.lineWidth = 0.55 * PX;
    if (GROUND.pathGuides !== false) for (const p of RD.paths) { path(p.pts); g.stroke(); } // WS-14: printed-map guide, off = packed snow only
    if (GROUND.trails === 'tracks') { // WS-14: trails as packed snow lanes (what is really on the ground), no coloured dashes
      g.setLineDash([]);
      for (const t of RD.trails) { path(t.pts); g.strokeStyle = 'rgba(214,224,236,0.75)'; g.lineWidth = (t.kind === 'ski' ? 2.2 : 2.6) * PX; g.stroke(); }
    } else
    for (const t of RD.trails) {
      g.setLineDash(t.kind === 'ski' ? [] : [5 * PX, 3 * PX]);
      if (t.kind === 'ski') {
        for (const off of [-0.45, 0.45]) { g.save(); g.translate(off * PX, 0); path(t.pts); g.strokeStyle = 'rgba(120,150,190,0.7)'; g.lineWidth = 0.25 * PX; g.stroke(); g.restore(); }
      } else { path(t.pts); g.strokeStyle = 'rgba(170,140,190,0.6)'; g.lineWidth = 1.4 * PX; g.stroke(); }
    }
    g.setLineDash([]);
    } // WS-16: end of the old road / path paint
    // Extra painted areas from resort.json (e.g. parking not mapped in OpenStreetMap).
    for (const a of I.paint) {
      if (a.polygon) { // WS-24: traced outline (local metres); no stall lines unless the source shows them
        g.beginPath();
        for (const r of [a.polygon, ...(a.holes || [])]) { r.forEach(([x, z], i) => (i ? g.lineTo(tx(x), tz(z)) : g.moveTo(tx(x), tz(z)))); g.closePath(); } // R1-ROADS: holes (grass islands in a yard)
        g.fillStyle = (P.winter ? a.color : a[`${SEASON}Color`]) || P.paint; g.fill('evenodd'); // WS-35: outside winter a.<season>Color or the palette's bare-ground paint colour
        if (first) CTL?.poly(a.polygon, a.layer || 'gravel', 1, 0.5); // WS-42: painted areas (parking) are gravel unless they say otherwise
        continue;
      }
      g.save(); g.translate(tx(a.center[0]), tz(a.center[1])); g.rotate(a.rot || 0);
      const [w, h] = a.size;
      g.fillStyle = '#dde3eb'; g.fillRect((-w / 2) * PX, (-h / 2) * PX, w * PX, h * PX);
      if (a.kind === 'parking') {
        g.strokeStyle = 'rgba(160,175,195,0.6)'; g.lineWidth = 0.3 * PX;
        for (let x = -w / 2 + 2; x <= w / 2 - 2; x += 3) { g.beginPath(); g.moveTo(x * PX, (-h / 2 + 1) * PX); g.lineTo(x * PX, (-h / 2 + 7) * PX); g.stroke(); }
      }
      g.restore();
    }
    // WS-35: clearings that only show without snow (sand, gravel yards), traced from a dated reference; soft edges
    for (const cl of P.clearings) { // { polygon } or { line, width, closed } (e.g. a sand track that is iced in winter)
      blurDraw(g, +((cl.blur ?? 1.5) * PX).toFixed(1), (g) => { // Safari/Mac ground paint: as for the areas above
        if (cl.polygon) { ring(cl.polygon); g.fillStyle = cl.color || '#c9b48c'; g.fill(); }
        else if (cl.line) { path(cl.closed ? [...cl.line, cl.line[0]] : cl.line); g.strokeStyle = cl.color || '#c9b48c'; g.lineWidth = (cl.width || 4) * PX; g.lineCap = g.lineJoin = 'round'; g.stroke(); }
      });
      if (first && CTL && cl.polygon) CTL.poly(cl.polygon, cl.layer || 'sand', 1, cl.blur ?? 1.5); // WS-42
      else if (first && CTL && cl.line) CTL.line(cl.line, cl.width || 4, cl.layer || 'sand', 1, cl.blur ?? 1.5, cl.closed);
    }
  }
  const L = liteWriter(cw, ch), F = I.land ? featherRing(cw, ch, W, D, PX) : null;
  const one = ch <= band + 2 * MARGIN, step = one ? ch : band;
  for (let y0 = 0; y0 < ch; y0 += step) { // a fresh canvas a band: read back once, it is never drawn on again (a GPU canvas read often turns to the processor)
    const y1 = Math.min(ch, y0 + step), top = one ? 0 : Math.max(0, y0 - MARGIN), bot = one ? ch : Math.min(ch, y1 + MARGIN);
    const bc = mk(cw, bot - top), g = bc.getContext('2d');
    s = 99; core(g, y0 === 0, top); F?.down(bc, y0, y1, top, mk);
    for (let r = y0; r < y1; r += 128) { const n = Math.min(128, y1 - r), d = g.getImageData(0, r - top, cw, n).data; L.put(d, 0, r, cw, n); F?.take(d, r, n); } // (strips: no second whole copy)
    free(bc);
  }
  free(sc); free(wc); free(shade?.c);
  const probe = F ? F.finish(L, mk) : null;
  return { lite: L.done(), ctl: CTL ? CTL.canvases : null, probe };
}

/**
 * WS-14 round 4b: the core's outer 60 m feathered into its own 10 m average, which is what the far landscape continues
 * with, so the boundary has no tone step (the far ground has no per-metre paint detail); and what the far landscape
 * reads of the final picture (landscape.js coreTex): the four 5 px border strips (ring 0 and the near-edge blend take
 * its exact colours) and the picture averaged into 10 m cells (the cover tones).
 * VIS-267 step 4: with the paint in bands, the 10 m copy is drawn band by band (down), the ring (the outer rows and
 * columns, kept from the bands: take) is feathered afterwards, part by part, with the same blits as before (each texel
 * lies in one ring strip), and the read-back's 10 m cells are a mean of the final texels (it was the canvas's downscale).
 */
function featherRing(cw, ch, W, D, PX) {
  const sw = Math.max(2, Math.round(W / 10)), sh = Math.max(2, Math.round(D / 10)), R = 60 * PX, step = 2 * PX;
  const Rr = (Math.ceil(R) + 3) & ~1; // the ring's rows and columns, even (whole 2x2 colour blocks), with a texel to spare
  const tEnd = Math.min(ch, Rr), bs = Math.max(tEnd, (ch - Rr) & ~1), lEnd = Math.min(cw, Rr), rs = Math.max(lEnd, (cw - Rr) & ~1);
  const part = (x0, y0, w, h) => (w > 0 && h > 0 ? { x0, y0, w, h, d: new Uint8ClampedArray(w * h * 4) } : null);
  const top = part(0, 0, cw, tEnd), bot = part(0, bs, cw, ch - bs), left = part(0, tEnd, lEnd, bs - tEnd), right = part(rs, tEnd, cw - rs, bs - tEnd);
  const P = [top, bot, left, right].filter(Boolean); // (disjoint: the corners are the top and bottom parts')
  const cellX = new Int32Array(cw); for (let x = 0; x < cw; x++) cellX[x] = Math.min(sw - 1, Math.floor((x * sw) / cw));
  const sum = new Float64Array(sw * sh * 3), n = new Float64Array(sw * sh);
  const add = (d, o, cy, x0, w, sign = 1, count = true) => { for (let i = 0; i < w; i++) { const c = cy + cellX[x0 + i], k = o + i * 4; sum[c * 3] += sign * d[k]; sum[c * 3 + 1] += sign * d[k + 1]; sum[c * 3 + 2] += sign * d[k + 2]; if (count) n[c]++; } };
  const mean = () => { const o = new Uint8ClampedArray(sw * sh * 4); for (let c = 0; c < sw * sh; c++) { for (let k = 0; k < 3; k++) o[c * 4 + k] = Math.round(sum[c * 3 + k] / Math.max(1, n[c])); o[c * 4 + 3] = 255; } return o; };
  let sm = null; // the 10 m copy the feather blends into: each band's rows drawn into its rows, as the whole canvas was (a mean per cell read 1-4 levels apart in the ring)
  return {
    /** a band's rows [y0, y1) (canvas bc, its first row `top`) into the 10 m copy */
    down(bc, y0, y1, top, mk) {
      if (!sm) { sm = mk(sw, sh); sm.getContext('2d').imageSmoothingQuality = 'high'; }
      sm.getContext('2d').drawImage(bc, 0, y0 - top, cw, y1 - y0, 0, (y0 * sh) / ch, sw, ((y1 - y0) * sh) / ch);
    },
    /** rows [r, r + rows) of the unfeathered canvas (RGBA, top-first) */
    take(d, r, rows) {
      for (let j = 0; j < rows; j++) {
        const y = r + j, o = j * cw * 4, cy = Math.min(sh - 1, Math.floor((y * sh) / ch)) * sw;
        add(d, o, cy, 0, cw);
        if (y < tEnd) top.d.set(d.subarray(o, o + cw * 4), y * cw * 4);
        else if (y >= bs) bot.d.set(d.subarray(o, o + cw * 4), (y - bs) * cw * 4);
        else { left?.d.set(d.subarray(o, o + lEnd * 4), (y - tEnd) * lEnd * 4); right?.d.set(d.subarray(o + rs * 4, o + cw * 4), (y - tEnd) * (cw - rs) * 4); }
      }
    },
    /** the feather, into the ground picture L; -> the probe */
    finish(L, mk) {
      for (const p of P) {
        const pc = mk(p.w, p.h), g = pc.getContext('2d'), pid = g.createImageData(p.w, p.h);
        pid.data.set(p.d); g.putImageData(pid, 0, 0);
        g.translate(-p.x0, -p.y0); g.imageSmoothingEnabled = true;
        const blit = (x, y, w, h) => g.drawImage(sm, (x / cw) * sw, (y / ch) * sh, (w / cw) * sw, (h / ch) * sh, x, y, w, h);
        for (let d = 0; d < R; d += step) {
          g.globalAlpha = (1 - d / R) ** 1.6;
          blit(d, d, cw - 2 * d, step); blit(d, ch - d - step, cw - 2 * d, step);
          blit(d, d + step, step, ch - 2 * d - 2 * step); blit(cw - d - step, d + step, step, ch - 2 * d - 2 * step);
        }
        const out = g.getImageData(0, 0, p.w, p.h).data;
        L.put(out, p.x0, p.y0, p.w, p.h);
        for (let j = 0; j < p.h; j++) { const o = j * p.w * 4, cy = Math.min(sh - 1, Math.floor(((p.y0 + j) * sh) / ch)) * sw; add(p.d, o, cy, p.x0, p.w, -1, false); add(out, o, cy, p.x0, p.w, 1, false); } // (the cell sums now hold the feathered texels)
        p.d = out; free(pc);
      }
      free(sm);
      const at = (x, y) => { const p = P.find((q) => x >= q.x0 && x < q.x0 + q.w && y >= q.y0 && y < q.y0 + q.h); return [p.d, ((y - p.y0) * p.w + (x - p.x0)) * 4]; };
      const strip = (x, y, w, h) => { const d = new Uint8ClampedArray(w * h * 4); for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) { const [s, k] = at(x + i, y + j); d.set(s.subarray(k, k + 4), (j * w + i) * 4); } return { x, y, w, h, d }; };
      return { width: cw, height: ch, strips: [[0, 0, cw, 5], [0, ch - 5, cw, 5], [0, 0, 5, ch], [cw - 5, 0, 5, ch]].map((a) => strip(...a)), small: { w: sw, h: sh, d: mean() } };
    },
  };
}

/**
 * VIS-267 (owner's pick): the painted canvas as brightness at full size + colour at half size (the JPEG / video 4:2:0
 * split; YCoCg as in van Waveren & Castano 2007), 1.5 bytes a texel instead of 4: Aurora's ground picture 25 MB on the
 * graphics chip instead of 63. Values stay the canvas's sRGB numbers (ground.js liteColor turns them back and linear).
 * Y = (R + 2G + B) / 4; Co = (R - B) / 2 + 128; Cg = (2G - R - B) / 4 + 128, each 2x2 block's Co and Cg averaged (back
 * within 1.5 of 255 where a block has one colour). Rows bottom-first (a DataTexture's row 0 is v = 0; the canvas's row 0
 * is v = 1).
 * ONE R8 texture: a fragment stage reads at most 16 textures on many adapters and the WebGL2 fallback, and
 * Aurora's ground with clouds is at 16 already, so the three planes share one: brightness in the lower rows, Co and Cg
 * side by side above, GAP texels between them filled with each plane's edge (as clamp-to-edge, for the mips and the
 * anisotropic taps). The layout: rows [0, h) brightness (columns past w repeat its last column), then GAP rows, then the
 * colour rows (Co at columns [0, w2), Cg at [w2 + GAP, 2 w2 + GAP)); every gap texel copies the nearest plane's edge.
 * VIS-267 step 4: filled a region at a time, straight into the one array (no whole brightness or colour copy beside
 * it: 18 MB at Aurora): put(rgba, x0, y0, w, h) a canvas region, rows top-first, x0 and y0 even (whole 2x2 blocks;
 * a region may be put again: the later wins); done() fills the gaps -> { w, h, aw, ah, gap, a: Uint8Array(aw * ah) }.
 */
export const LITE_GAP = 128; // texels between the planes (each side's half filled with its edge): clean up to mip level 6
export function liteWriter(w, h, gap = LITE_GAP) {
  const w2 = Math.ceil(w / 2), h2 = Math.ceil(h / 2), aw = 2 * w2 + gap, ah = h + gap + h2, a = new Uint8Array(aw * ah);
  const c0 = h + gap, half = h + (gap >> 1), split = w2 + (gap >> 1);
  return {
    put(d, x0, y0, rw, rh) {
      for (let j = 0; j < rh; j++) {
        const out = (h - 1 - (y0 + j)) * aw + x0, src = j * rw * 4;
        for (let i = 0; i < rw; i++) { const k = src + i * 4; a[out + i] = (d[k] + 2 * d[k + 1] + d[k + 2] + 2) >> 2; }
      }
      for (let j = 0; j < rh; j += 2) {
        const rows = j + 1 < rh ? 2 : 1, o = (c0 + h2 - 1 - ((y0 + j) >> 1)) * aw;
        for (let i = 0; i < rw; i += 2) {
          let co = 0, cg = 0, n = 0;
          for (let jj = 0; jj < rows; jj++) for (let ii = 0; ii < 2 && i + ii < rw; ii++) { const k = ((j + jj) * rw + i + ii) * 4, R = d[k], G = d[k + 1], B = d[k + 2]; co += R - B; cg += 2 * G - R - B; n++; }
          const b = (x0 + i) >> 1;
          a[o + b] = Math.min(255, Math.max(0, Math.round(co / (2 * n) + 128)));
          a[o + w2 + gap + b] = Math.min(255, Math.max(0, Math.round(cg / (4 * n) + 128)));
        }
      }
    },
    done() {
      for (let r = 0; r < h; r++) a.fill(a[r * aw + w - 1], r * aw + w, (r + 1) * aw); // past the brightness: its last column
      for (let r = h; r < half; r++) a.copyWithin(r * aw, (h - 1) * aw, h * aw); // the gap's lower half: brightness's last row
      for (let r = c0; r < ah; r++) { const o = r * aw; a.fill(a[o + w2 - 1], o + w2, o + split); a.fill(a[o + w2 + gap], o + split, o + w2 + gap); } // between Co and Cg: each one's edge
      for (let r = half; r < c0; r++) a.copyWithin(r * aw, c0 * aw, (c0 + 1) * aw); // the gap's upper half: the first colour row
      return { w, h, aw, ah, gap, a };
    },
  };
}
/** A whole canvas as the ground picture (read in strips of 128 rows, so the copy never holds a second whole canvas) */
export function toLite(canvas) {
  const w = canvas.width, h = canvas.height, g = canvas.getContext('2d'), L = liteWriter(w, h);
  for (let r0 = 0; r0 < h; r0 += 128) { const rows = Math.min(128, h - r0); L.put(g.getImageData(0, r0, w, rows).data, 0, r0, w, rows); } // (even: a 2x2 block never spans two strips)
  return L.done();
}

const domCanvas = (w, h) => Object.assign(document.createElement('canvas'), { width: w, height: h });
/** The paint on this thread (DOM canvases): the fallback, and what tests call. -> { lite, ctl, probe } */
export const paintHere = (I) => paintGround(I, domCanvas);
/** VIS-267 step 4: a paint's input as one string. paintGround is a pure function of it (seeded random stream, nothing
 *  read from the page), so an equal key paints equal pixels: main.js asks for no repaint whose key is the newest paint's
 *  (one paint per start). Typed arrays (the trees shown) go in as their length and an FNV-1a hash of their words. */
export function paintKey(I) {
  const fnv = (v) => {
    const a = v.byteOffset % 4 || v.byteLength % 4 ? new Uint8Array(v.buffer, v.byteOffset, v.byteLength) : new Uint32Array(v.buffer, v.byteOffset, v.byteLength / 4);
    let h = 2166136261; for (let i = 0; i < a.length; i++) h = Math.imul(h ^ a[i], 16777619);
    return (h >>> 0).toString(36);
  };
  return JSON.stringify(I, (k, v) => (ArrayBuffer.isView(v) ? `#${v.length}:${fnv(v)}` : v));
}
/** Can a worker here paint 2D and hand the picture back? (the worker checks its own 2D context too) */
export const canPaintOff = (G = globalThis) => typeof G.Worker === 'function' && typeof G.OffscreenCanvas === 'function' && typeof G.OffscreenCanvas.prototype?.transferToImageBitmap === 'function';
/** The longest a paint worker may take before the paint is done here instead: a worker that dies silently or never
 *  answers must not hold the start (main.js) or leave a season switch `switching`. VIS-267 step 4: 3 s, was 20: on an
 *  iPhone 13 Pro the worker (WebKit's OffscreenCanvas 2D, apparently painted without the GPU) never answered within 20 s
 *  on the phone set, so each start waited 20 s and then painted here in ~2 s. A sound worker answers in 0.5-1.5 s (this
 *  PC at 4x CPU, phones' "Shaping the terrain" 1-2 s). Judged by the answer's own time, never by the device.
 *  PAINT_LATE_MS: a paint after the start (a season switch under its cover, a repaint) is never done here (below). */
export const PAINT_MS = 3000, PAINT_LATE_MS = 10000;
const closeAll = (r) => { for (const im of r?.ctl || []) im?.close?.(); }; // (the picture is plain arrays: nothing to close)
let noWorker = false; // VIS-267 step 4: a worker did not run on this page (it never loaded, or could not take the input): no more tries
/**
 * The paint in a worker (one per paint, closed after it: nothing of it stays on the main thread). Resolves once with
 * { lite: { w, h, aw, ah, gap, a } (liteWriter), ctl: [2] | null, probe | null }. A worker that cannot paint, cannot be
 * reached, sends an answer that cannot be read or has not answered within `ms` is closed and the paint is done here
 * (paintHere); an answer after that is ignored (its pictures closed).
 * VIS-267 step 4 (an iPhone 13 Pro's season switch: its worker threw "The object is in an invalid state", the paint
 * was done here again, and the graphics were lost 0.6 s later): `start: false` (any paint after the start) never does a
 * failed or late worker's paint here; it resolves null, the ground keeps its picture and main.js says so once. Decided by
 * what the worker did, never by the device. Where no worker can paint at all (`can`), it is painted here, as at the start.
 * opts (tests): { start, ms, can, worker() -> Worker, here() -> result }
 */
export function paintAsync(I, { start = true, ms = start ? PAINT_MS : PAINT_LATE_MS, can = canPaintOff(), worker = () => new Worker(new URL('./groundpaint-worker.js', import.meta.url), { type: 'module' }), here: local = () => paintHere(I) } = {}) {
  return new Promise((resolve, reject) => {
    let settled = false, w = null, timer = 0;
    const finish = (fn) => { if (settled) return false; settled = true; clearTimeout(timer); try { w?.terminate(); } catch { /* errors:ignore: already closed */ } fn(); return true; };
    const here = () => finish(() => { try { resolve(local()); } catch (err) { reject(err); } });
    // a paint that failed in the worker or came too late: at the start painted here; after it, the ground keeps its picture
    const failed = (why) => { if (!settled) console.warn(`[ground paint] ${why}: ${start ? 'painted on the main thread' : 'the ground keeps its picture'}`); if (start) here(); else finish(() => resolve(null)); };
    const cannot = () => { noWorker = true; here(); }; // a worker that cannot run here at all: this device paints here, as with no worker (`can`)
    if (!can || noWorker) return here();
    try { w = worker(); } catch { return cannot(); } // errors:ignore: no module workers: painted here
    w.onmessage = (e) => {
      if (e.data?.error) failed(String(e.data.error)); // (the iPhone's InvalidStateError: the paint itself failed)
      else if (!finish(() => resolve(e.data))) closeAll(e.data); // a late answer (painted here already)
    };
    w.onerror = (e) => { e?.preventDefault?.(); if (settled) return; console.warn('[ground paint] the worker did not run: painted on the main thread'); cannot(); }; // a worker that does not load (old Safari: no module workers)
    w.onmessageerror = () => failed('an answer that cannot be read'); // (a start never waits for nothing)
    timer = setTimeout(() => failed(`no answer from the worker in ${ms} ms`), ms); // a worker that died silently, or paints too slowly (VIS-267: closed, its answer dropped)
    try { w.postMessage(I); } catch { cannot(); } // errors:ignore: an input a worker cannot receive (DataCloneError) = painted here
  });
}
